// État temps réel : une connexion WebSocket /ws partagée par toute l'application.
// Le serveur envoie l'état complet chaque seconde ; on garde en plus un court historique
// des débits (sparklines) et les derniers événements.
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { notifyUnauthorized } from "../api/client";
import type { GwEvent, StateMessage } from "../api/types";

export const HISTORY_POINTS = 120; // 2 minutes à 1 message par seconde
const MAX_EVENTS = 300;

export type Connection = "connecting" | "open" | "closed";

export interface Live {
  state: StateMessage | null;
  connection: Connection;
  /** Débit entrant par entrée (kb/s), du plus ancien au plus récent. */
  history: Record<string, number[]>;
  /** Derniers événements, du plus récent au plus ancien. */
  events: GwEvent[];
}

const LiveContext = createContext<Live | null>(null);

export function appendHistory(history: Record<string, number[]>, msg: StateMessage): Record<string, number[]> {
  const next: Record<string, number[]> = {};
  for (const input of msg.inputs) {
    const prev = history[input.id] ?? [];
    next[input.id] = [...prev.slice(-(HISTORY_POINTS - 1)), input.live.bitrate_in_kbps ?? 0];
  }
  return next;
}

export function mergeEvents(current: GwEvent[], incoming: GwEvent[]): GwEvent[] {
  if (incoming.length === 0) return current;
  const seen = new Set(current.map((e) => e.id));
  const fresh = incoming.filter((e) => !seen.has(e.id)).reverse();
  return [...fresh, ...current].slice(0, MAX_EVENTS);
}

export function LiveProvider({ children, authVersion }: { children: ReactNode; authVersion: number }) {
  const [live, setLive] = useState<Live>({ state: null, connection: "connecting", history: {}, events: [] });
  const retry = useRef(0);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let timer: number | undefined;
    let stopped = false;

    const connect = () => {
      // authentifié par le cookie de session, que le navigateur joint à la connexion
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      ws = new WebSocket(`${proto}://${window.location.host}/ws`);
      setLive((l) => ({ ...l, connection: "connecting" }));
      ws.onopen = () => {
        retry.current = 0;
        setLive((l) => ({ ...l, connection: "open" }));
      };
      ws.onmessage = (e) => {
        const msg = JSON.parse(e.data as string) as StateMessage;
        if (msg.type !== "state") return;
        setLive((l) => ({
          state: msg,
          connection: "open",
          history: appendHistory(l.history, msg),
          events: mergeEvents(l.events, msg.events),
        }));
      };
      ws.onclose = (e) => {
        setLive((l) => ({ ...l, connection: "closed" }));
        if (stopped) return;
        if (e.code === 4401) {
          // session révoquée, ou droits modifiés par un administrateur : on revérifie la session
          notifyUnauthorized();
          return;
        }
        // reconnexion : 1, 2, 4, 8 puis 10 s
        const delay = Math.min(10_000, 1000 * 2 ** retry.current++);
        timer = window.setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      ws?.close();
    };
  }, [authVersion]);

  return <LiveContext.Provider value={live}>{children}</LiveContext.Provider>;
}

export function useLive(): Live {
  const live = useContext(LiveContext);
  if (!live) throw new Error("useLive hors de LiveProvider");
  return live;
}
